import { foldToAscii } from '@luna/utils';

import { HTTP_STATUS_CODE } from '../HttpStatusCode';
import { tokenRefreshQueue } from './TokenRefreshQueue';
import type { TokenRefresh } from './types';

export const DEFAULT_REFRESHABLE_STATUS_CODES: number[] = [HTTP_STATUS_CODE.UNAUTHORIZED];

type ExtendedFetchOptions = {
    url: string;
    getFetchOptions: () => RequestInit;
    refreshToken?: TokenRefresh;
    refreshableStatusCodes?: number[];
};

function getEntries(headers: HeadersInit): [string, string][] {
    if (Array.isArray(headers)) {
        return headers;
    }

    if (headers instanceof Headers) {
        return Array.from(headers.entries());
    }

    return Object.entries(headers);
}

function toSafeHeaders(headers: HeadersInit = {}): Record<string, string> {
    const entries = getEntries(headers);
    return Object.fromEntries(
        entries.map(([key, value]) => {
            return [key, foldToAscii(value)];
        }),
    );
}

export const extendedFetch = async ({
    url,
    getFetchOptions,
    refreshToken,
    refreshableStatusCodes = DEFAULT_REFRESHABLE_STATUS_CODES,
}: ExtendedFetchOptions): Promise<Response> => {
    const fetchOptions = getFetchOptions();

    const fetchResponse = await fetch(url, {
        ...fetchOptions,
        headers: toSafeHeaders(fetchOptions.headers),
    });
    if (!refreshableStatusCodes.includes(fetchResponse.status)) {
        return fetchResponse;
    }
    if (!refreshToken) {
        return fetchResponse;
    }

    try {
        await tokenRefreshQueue.refreshToken(refreshToken);
        return fetch(url, getFetchOptions());
    } catch {
        return fetchResponse;
    }
};
